import * as React from "react";
import { Maximize2, Network } from "lucide-react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { AnimatePresence, motion } from "framer-motion";

import { useWorkspaceStore } from "@/features/diagnosis/store/workspace";
import { cn } from "@/lib/utils";
import type { GraphEdge, GraphNode } from "@/types/api";

interface WorkspaceLayoutProps {
  intake: React.ReactNode;
  results: React.ReactNode;
  graph: React.ReactNode;
  graphNodeCount?: number;
  graphEdgeCount?: number;
  graphNodes?: GraphNode[];
  graphEdges?: GraphEdge[];
  topPath?: string[];
}

function useIsMobile(): boolean {
  const [mobile, setMobile] = React.useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener("change", handler);
    setMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return mobile;
}

// Lazy-load the mobile graph view (includes react-zoom-pan-pinch + G6 render)
const MobileGraphView = React.lazy(() =>
  import("@/features/graph/components/MobileGraphView").then((m) => ({
    default: m.MobileGraphView,
  })),
);

export function WorkspaceLayout({
  intake,
  results,
  graph,
  graphNodeCount = 0,
  graphEdgeCount = 0,
  graphNodes = [],
  graphEdges = [],
  topPath,
}: WorkspaceLayoutProps) {
  const panelSizes = useWorkspaceStore((s) => s.panelSizes);
  const setPanelSizes = useWorkspaceStore((s) => s.setPanelSizes);
  const graphFullscreen = useWorkspaceStore((s) => s.graphFullscreen);
  const isMobile = useIsMobile();
  const [mobileGraphOpen, setMobileGraphOpen] = React.useState(false);

  const handleLayout = React.useCallback(
    (sizes: number[]) => {
      if (sizes.length === 3) {
        setPanelSizes([sizes[0]!, sizes[1]!, sizes[2]!]);
      }
    },
    [setPanelSizes],
  );

  // Mobile layout
  if (isMobile) {
    return (
      <>
        <main className="bg-background flex-1">
          <section aria-label="Clinical intake" className="border-border border-b p-3">
            {intake}
          </section>
          <section aria-label="Differential diagnosis" className="border-border border-b p-3">
            {results}
          </section>

          {/* Graph card — only shown after diagnosis results are available */}
          {graphNodeCount > 0 && (
            <section
              aria-label="Reasoning graph"
              className="border-border flex flex-col items-center justify-center gap-3 border-b px-4 py-10"
            >
              <div className="bg-muted/30 flex h-14 w-14 items-center justify-center rounded-full">
                <Network className="text-muted-foreground h-6 w-6" />
              </div>
              <div className="text-center">
                <p className="text-foreground text-sm font-medium">Reasoning graph</p>
                <p className="text-muted-foreground text-xs">
                  {graphNodeCount} nodes &middot; {graphEdgeCount} edges
                </p>
              </div>
              <button
                type="button"
                onClick={() => setMobileGraphOpen(true)}
                className="bg-primary text-primary-foreground mt-1 flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium shadow-lg"
              >
                <Maximize2 className="h-4 w-4" />
                Explore graph
              </button>
            </section>
          )}
        </main>

        {/* Fullscreen graph — renders G6 to static image, wraps in
            react-zoom-pan-pinch for smooth native touch zoom/pan. */}
        {mobileGraphOpen && graphNodes.length > 0 && (
          <React.Suspense
            fallback={
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 9999,
                  backgroundColor: "hsl(var(--background))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div className="border-primary h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
              </div>
            }
          >
            <MobileGraphView
              nodes={graphNodes}
              edges={graphEdges}
              topPath={topPath}
              onClose={() => setMobileGraphOpen(false)}
            />
          </React.Suspense>
        )}
      </>
    );
  }

  // Desktop layout — unchanged
  return (
    <main className="bg-background flex min-h-0 flex-1">
      <AnimatePresence mode="wait" initial={false}>
        {graphFullscreen ? (
          <motion.div
            key="fullscreen"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="min-w-0 flex-1"
          >
            {graph}
          </motion.div>
        ) : (
          <motion.div
            key="split"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="min-w-0 flex-1"
          >
            <PanelGroup
              direction="horizontal"
              onLayout={handleLayout}
              className="h-full"
              autoSaveId={undefined}
            >
              <Panel defaultSize={panelSizes[0]} minSize={16} maxSize={45} className="min-w-0">
                <DesktopPanel label="Clinical intake">{intake}</DesktopPanel>
              </Panel>
              <ResizeHandle />
              <Panel defaultSize={panelSizes[1]} minSize={18} className="min-w-0">
                <DesktopPanel label="Differential diagnosis">{results}</DesktopPanel>
              </Panel>
              <ResizeHandle />
              <Panel defaultSize={panelSizes[2]} minSize={18} className="min-w-0">
                <DesktopPanel label="Reasoning graph" noPadding>
                  {graph}
                </DesktopPanel>
              </Panel>
            </PanelGroup>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

function DesktopPanel({
  label,
  children,
  noPadding,
}: {
  label: string;
  children: React.ReactNode;
  noPadding?: boolean;
}) {
  return (
    <section
      aria-label={label}
      className={cn(
        "bg-background flex h-full min-w-0 flex-col overflow-hidden",
        !noPadding && "p-4",
      )}
    >
      {children}
    </section>
  );
}

function ResizeHandle() {
  return (
    <PanelResizeHandle className="bg-border data-[resize-handle-active]:bg-primary group relative w-px outline-none transition-colors">
      <div className="absolute inset-y-0 -left-1 -right-1 cursor-col-resize" />
      <div className="bg-border group-hover:bg-primary/60 pointer-events-none absolute left-1/2 top-1/2 h-8 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors" />
    </PanelResizeHandle>
  );
}
